import { Text } from "@solomon-labs/design/components/ui";
import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useOrgAuth } from "../../hooks/auth";
import {
    type OAuthConfig,
    resolveOrgAuthUserMessage
} from "../../services/orgAuthService";
import { Spinner } from "../ui/spinner";

import { OrgAuthShell } from "./OrgAuthShell";

interface OrgAuthCallbackPageProps {
    config: OAuthConfig;
    /** "Client Console" / "Issuer Console" — refines membership-related errors. */
    appLabel: string;
}

export function OrgAuthCallbackPage({
    config,
    appLabel
}: OrgAuthCallbackPageProps): ReactElement {
    const [searchParams] = useSearchParams();
    const { handleCallback } = useOrgAuth(config);
    const [error, setError] = useState<string | null>(null);
    const called = useRef(false);

    useEffect(() => {
        if (called.current) return;
        called.current = true;

        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const oauthError = searchParams.get("error");
        const oauthErrorDescription = searchParams.get("error_description");

        if (oauthError) {
            setError(
                resolveOrgAuthUserMessage({
                    code: oauthError,
                    message: oauthErrorDescription,
                    appLabel
                })
            );
            return;
        }

        if (!code || !state) {
            setError(
                resolveOrgAuthUserMessage({
                    message: "Invalid callback — missing authorization code."
                })
            );
            return;
        }

        handleCallback(code, state).catch((err: unknown) => {
            const message =
                err instanceof Error ? err.message : "Authentication failed.";
            setError(resolveOrgAuthUserMessage({ message, appLabel }));
        });
    }, [appLabel, handleCallback, searchParams]);

    if (error) {
        return (
            <OrgAuthShell title="Sign in failed" intro={error}>
                <Text
                    variant="small"
                    className="text-foreground-base underline underline-offset-2 hover:text-foreground-muted"
                    render={<Link to="/login" />}
                >
                    Try again
                </Text>
            </OrgAuthShell>
        );
    }

    return (
        <OrgAuthShell intro="Completing sign in…">
            <div className="flex flex-col items-center gap-3 py-2">
                <Spinner />
                <Text variant="small" className="text-foreground-imprint">
                    Signing in…
                </Text>
            </div>
        </OrgAuthShell>
    );
}
